import React, { useState, useEffect, useMemo } from 'react';
import { getOrderPayableTotal } from '../../utils/orderAmount';
import { getCategoryChip } from '../../utils/customerCategory';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Divider,
  Chip,
  Grid,
  Avatar,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  ShoppingCart as OrderIcon,
  LocationOn as LocationIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Store as StoreIcon,
  Info as InfoIcon,
  Notes as NotesIcon,
  Save as SaveIcon,
  LocalShipping as LocalShippingIcon,
  CardGiftcard as CardGiftcardIcon,
  SwapHoriz as TransferIcon,
  Build as ActionsIcon,
  AccessTime as TimeSlotIcon,
  CallSplit as PartialDeliveryIcon
} from '@mui/icons-material';
import { GoogleMap, useJsApiLoader, Polyline, Marker } from '@react-google-maps/api';
import { cacheEndpoints } from '../../utils/apiCache';
import api from '../../utils/api';
import { useApp } from '../../context/AppContext';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyBC28IfOmHnlcxvQo1sW9sduMvJ9jENTlE';
const TRACKING_MAP_CONTAINER_STYLE = { width: '100%', height: '320px', borderRadius: 8 };

const RIDER_BIKE_ICON_URL = '/rider-scooter.png';

function DeliveryTrackingMap({ locations }) {
  const [map, setMap] = useState(null);
  const [animatedRiderPosition, setAnimatedRiderPosition] = useState(null);
  const animationFrameRef = React.useRef(null);
  const animationStartRef = React.useRef(null);
  const animationFromRef = React.useRef(null);
  const animationToRef = React.useRef(null);
  const didInitialViewportRef = React.useRef(false);
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-tracking',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY
  });

  const path = useMemo(() => {
    if (!locations?.length) return [];

    // Render route exactly as recorded: keep all valid lat/lng points in timestamp order.
    const points = locations
      .filter((loc) => loc?.lat != null && loc?.lng != null)
      .map((loc) => ({
        lat: Number(loc.lat),
        lng: Number(loc.lng),
        timestamp: Number(loc.timestamp) || null
      }))
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng))
      .sort((a, b) => {
        if (a.timestamp == null && b.timestamp == null) return 0;
        if (a.timestamp == null) return 1;
        if (b.timestamp == null) return -1;
        return a.timestamp - b.timestamp;
      });

    return points.map(({ lat, lng }) => ({ lat, lng }));
  }, [locations]);

  const lastPosition = path.length > 0 ? path[path.length - 1] : null;
  const center = animatedRiderPosition || lastPosition || { lat: 17.4322, lng: 78.5651 };

  // Always keep the rider in focus while refreshing.
  useEffect(() => {
    if (!map || !lastPosition) return;
    // panTo avoids changing zoom; keeps the view "following" the rider.
    map.panTo(lastPosition);
  }, [map, lastPosition]);

  useEffect(() => {
    if (!lastPosition) {
      setAnimatedRiderPosition(null);
      return undefined;
    }

    // First point or large jump: set immediately.
    if (!animatedRiderPosition) {
      setAnimatedRiderPosition(lastPosition);
      return undefined;
    }

    const ANIMATION_MS = 900;
    animationStartRef.current = null;
    animationFromRef.current = animatedRiderPosition;
    animationToRef.current = lastPosition;

    const animate = (timestamp) => {
      if (!animationStartRef.current) animationStartRef.current = timestamp;
      const elapsed = timestamp - animationStartRef.current;
      const progress = Math.min(elapsed / ANIMATION_MS, 1);
      // Ease-in-out for smoother movement
      const eased = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      const from = animationFromRef.current;
      const to = animationToRef.current;
      const lat = from.lat + (to.lat - from.lat) * eased;
      const lng = from.lng + (to.lng - from.lng) * eased;
      setAnimatedRiderPosition({ lat, lng });

      if (progress < 1) {
        animationFrameRef.current = window.requestAnimationFrame(animate);
      }
    };

    if (animationFrameRef.current) {
      window.cancelAnimationFrame(animationFrameRef.current);
    }
    animationFrameRef.current = window.requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [lastPosition]);

  const onMapLoad = React.useCallback((mapInstance) => {
    setMap(mapInstance);
    if (didInitialViewportRef.current) return;
    didInitialViewportRef.current = true;

    // Initial viewport: show the full route if we have one, otherwise focus rider/default center.
    if (path.length >= 2) {
      const bounds = new window.google.maps.LatLngBounds();
      path.forEach((p) => bounds.extend(new window.google.maps.LatLng(p.lat, p.lng)));
      mapInstance.fitBounds(bounds);
      const listener = window.google.maps.event.addListener(mapInstance, 'idle', () => {
        const z = mapInstance.getZoom();
        if (z > 17) mapInstance.setZoom(17);
        window.google.maps.event.removeListener(listener);
      });
    } else if (lastPosition) {
      mapInstance.panTo(lastPosition);
      mapInstance.setZoom(15);
    }
  }, [path]);

  const handleZoomIn = () => {
    if (map) {
      const z = map.getZoom();
      map.setZoom(Math.min((z || 14) + 1, 18));
    }
  };

  const handleZoomOut = () => {
    if (map) {
      const z = map.getZoom();
      map.setZoom(Math.max((z || 14) - 1, 4));
    }
  };

  if (loadError) {
    return (
      <Box sx={{ ...TRACKING_MAP_CONTAINER_STYLE, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100', borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary">Failed to load map</Typography>
      </Box>
    );
  }
  if (!isLoaded) {
    return (
      <Box sx={{ ...TRACKING_MAP_CONTAINER_STYLE, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100', borderRadius: 2 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }
  return (
    <Box sx={{ borderRadius: 2, overflow: 'hidden', border: 1, borderColor: 'divider', position: 'relative' }}>
      <GoogleMap
        mapContainerStyle={TRACKING_MAP_CONTAINER_STYLE}
        center={center}
        zoom={14}
        onLoad={onMapLoad}
        options={{
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: false
        }}
      >
        {path.length > 1 && (
          <Polyline
            path={path}
            options={{
              strokeColor: '#1565c0',
              strokeOpacity: 1,
              strokeWeight: 4,
              geodesic: true
            }}
          />
        )}
        {animatedRiderPosition && (
          <Marker
            position={animatedRiderPosition}
            title="Rider"
            icon={{
              url: RIDER_BIKE_ICON_URL,
              scaledSize: window.google?.maps ? new window.google.maps.Size(34, 34) : undefined,
              anchor: window.google?.maps ? new window.google.maps.Point(17, 17) : undefined
            }}
            zIndex={999}
          />
        )}
      </GoogleMap>
      {/* Custom zoom in / zoom out buttons */}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          zIndex: 10
        }}
      >
        <Button
          variant="contained"
          size="small"
          onClick={handleZoomIn}
          sx={{
            minWidth: 36,
            width: 36,
            height: 36,
            p: 0,
            borderRadius: 1,
            boxShadow: 2,
            '&:hover': { bgcolor: 'primary.dark' }
          }}
        >
          +
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleZoomOut}
          sx={{
            minWidth: 36,
            width: 36,
            height: 36,
            p: 0,
            borderRadius: 1,
            boxShadow: 2,
            '&:hover': { bgcolor: 'primary.dark' }
          }}
        >
          −
        </Button>
      </Box>
    </Box>
  );
}

const OrderModal = ({ open, onClose, order, onOrderUpdated, onPartialDelivery }) => {
  const { actions, state } = useApp();
  // Store transfer & time slot change are restricted to super admin only
  const isSuperAdmin = state.user?.role === 'super_admin' || state.user?.role === 'super-admin';
  const [orderStatus, setOrderStatus] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Transfer functionality state
  const [showTransfer, setShowTransfer] = useState(false);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');
  const [transferring, setTransferring] = useState(false);

  // Time slot change functionality state
  const [showTimeSlotChange, setShowTimeSlotChange] = useState(false);
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedTimeSlotId, setSelectedTimeSlotId] = useState('');
  const [timeSlotsLoading, setTimeSlotsLoading] = useState(false);

  const [showTrackingMap, setShowTrackingMap] = useState(false);
  const [trackingLocations, setTrackingLocations] = useState([]);

  // Initialize order status when order changes
  useEffect(() => {
    if (order) {
      setOrderStatus(getOrderStatus(order));
      setShowTrackingMap(false);
      setTrackingLocations(
        Array.isArray(order.deliveryLocations) ? order.deliveryLocations : []
      );
      // Reset transfer state when order changes or when transfer is not allowed
      if (order.orderFrom !== 'PILOT' || getOrderStatus(order) !== 'pending') {
        setShowTransfer(false);
        setSelectedStore('');
      }
      // Always reset time slot change state when order changes
      setShowTimeSlotChange(false);
      setSelectedTimeSlotId('');
    }
  }, [order]);

  // Fetch stores when modal opens
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const storesData = await cacheEndpoints.stores.getAll();
        setStores(storesData);
      } catch (error) {
        console.error('Error fetching stores:', error);
      }
    };

    if (open) {
      fetchStores();
    } else {
      // Reset state when modal closes
      setShowTimeSlotChange(false);
      setSelectedTimeSlotId('');
      setShowTransfer(false);
      setSelectedStore('');
      setShowTrackingMap(false);
    }
  }, [open]);

  // Fetch time slots when time slot change UI is shown
  useEffect(() => {
    const fetchTimeSlots = async () => {
      if (!showTimeSlotChange || !order) return;

      const storeId = order.storeId || order.branch_id;
      const deliveryDate = order.deliveryDate;

      if (!storeId || !deliveryDate) {
        console.warn('Cannot fetch time slots: missing storeId or deliveryDate');
        actions.showSnackbar('Cannot fetch time slots: missing store or delivery date', 'warning');
        return;
      }

      try {
        setTimeSlotsLoading(true);
        // Format delivery date as YYYY-MM-DD if needed
        let deliveryDateStr = deliveryDate;
        if (typeof deliveryDate === 'number') {
          const date = new Date(deliveryDate);
          deliveryDateStr = date.toISOString().split('T')[0];
        } else if (typeof deliveryDate === 'string' && !deliveryDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const date = new Date(deliveryDate);
          deliveryDateStr = date.toISOString().split('T')[0];
        }

        const response = await api.get(`/time-slots?storeId=${storeId}&deliveryDate=${deliveryDateStr}`);
        
        if (response.data.success) {
          // Filter only active slots
          let activeSlots = (response.data.timeSlots || []).filter(slot => slot.active);
          
          // If current slot is not in active slots, add it so it's visible
          const currentSlotId = order.timeSlotId;
          if (currentSlotId) {
            const currentSlotInList = activeSlots.find(slot => slot.id === currentSlotId);
            if (!currentSlotInList) {
              // Find the current slot in all slots (even if inactive)
              const allSlots = response.data.timeSlots || [];
              const currentSlot = allSlots.find(slot => slot.id === currentSlotId);
              if (currentSlot) {
                // Add current slot at the beginning, marked as disabled if inactive
                activeSlots = [{ ...currentSlot, isDisabled: !currentSlot.active }, ...activeSlots];
              }
            }
          }
          
          setTimeSlots(activeSlots);
          // Set current time slot as selected
          setSelectedTimeSlotId(order.timeSlotId || '');
        }
      } catch (error) {
        console.error('Error fetching time slots:', error);
        actions.showSnackbar('Error fetching time slots', 'error');
      } finally {
        setTimeSlotsLoading(false);
      }
    };

    if (showTimeSlotChange && order) {
      fetchTimeSlots();
    }
  }, [showTimeSlotChange, order, actions]);

  // Refresh tracking points while map is visible
  useEffect(() => {
    if (!open || !showTrackingMap || !order) return undefined;

    const orderId = order.orderId || order.id;
    if (!orderId) return undefined;

    let isMounted = true;
    let timerId = null;

    const refreshTrackingPoints = async () => {
      try {
        const response = await api.get(`/orders/${orderId}`);
        const latestLocations = response?.data?.order?.deliveryLocations;
        if (isMounted && Array.isArray(latestLocations)) {
          setTrackingLocations(latestLocations);
        }
      } catch (error) {
        console.warn('Error refreshing delivery locations:', error?.message || error);
      } finally {
        if (isMounted) {
          timerId = setTimeout(refreshTrackingPoints, 5000);
        }
      }
    };

    refreshTrackingPoints();

    return () => {
      isMounted = false;
      if (timerId) clearTimeout(timerId);
    };
  }, [open, showTrackingMap, order]);

  if (!order) return null;

  const deliveryTypeRawValue = order.deliveryType ?? order.delivery_type;
  const deliveryTypeRaw =
    deliveryTypeRawValue === null || deliveryTypeRawValue === undefined
      ? ''
      : String(deliveryTypeRawValue);
  const deliveryTypeNormalized = deliveryTypeRaw.trim().toLowerCase();
  const isSelfPickup = deliveryTypeNormalized === 'self_pickup';
  const deliveryTypeLabel =
    deliveryTypeNormalized === 'delivery'
      ? 'Home Delivery'
      : isSelfPickup
        ? 'Self Pickup'
        : (deliveryTypeRaw || 'N/A');

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else if (timestamp?.toDate) {
      date = timestamp.toDate();
    } else {
      return 'N/A';
    }
    
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime12Hour = (timeString) => {
    if (!timeString) return 'N/A';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDeliveryDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    let date;
    if (typeof dateString === 'number') {
      date = new Date(dateString);
    } else if (typeof dateString === 'string') {
      date = new Date(dateString);
    } else if (dateString?.toDate) {
      date = dateString.toDate();
    } else {
      return 'N/A';
    }
    
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  const getOrderStatus = (order) => {
    return order.order_status || 'pending';
  };

  const getOrderAmount = (order) => getOrderPayableTotal(order);

  const getOrderDate = (order) => {
    return order.timeStamp || order.createdAt;
  };

  const getOrderId = (order) => {
    return order.orderId || order.id || 'N/A';
  };

  const getDeliveryDate = (order) => {
    return order.deliveryDate || 'N/A';
  };

  const getPaymentMethod = (order) => {
    return order.paymentMethod || 'N/A';
  };

  const getOrderStatusColor = (status) => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'delivered':
      case 'completed':
        return 'success';
      case 'out_for_delivery':
      case 'processing':
      case 'confirmed':
        return 'warning';
      case 'assigned':
        return 'success';
      case 'returned':
        return 'warning'; // Orange color for returned orders
      case 'cancelled':
      case 'failed':
        return 'error';
      case 'pending':
        return 'info';
      case 'partial_delivery':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'processing', label: 'Processing' },
    { value: 'assigned', label: 'Assigned' },
    { value: 'out_for_delivery', label: 'Out for Delivery' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'returned', label: 'Returned' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const handleSaveStatus = async () => {
    if (!order) return;

    const statusChanged = orderStatus !== getOrderStatus(order);
    const timeSlotChanged = selectedTimeSlotId && selectedTimeSlotId !== (order.timeSlotId || '');
    
    if (!statusChanged && !timeSlotChanged) {
      actions.showSnackbar('No changes to save', 'info');
      return;
    }

    setSaving(true);
    try {
      const orderIdToUpdate = getOrderId(order);
      
      // Prepare update data
      const updateData = {};
      
      // Add status if changed
      if (statusChanged) {
        updateData.status = orderStatus;
      }
      
      // Add time slot if changed
      if (timeSlotChanged) {
        const selectedSlot = timeSlots.find(slot => slot.id === selectedTimeSlotId);
        if (selectedSlot) {
          updateData.timeSlotId = selectedTimeSlotId;
          updateData.timeSlotDetails = {
            id: selectedSlot.id,
            startTime: selectedSlot.startTime,
            endTime: selectedSlot.endTime,
            displayTime: selectedSlot.displayTime,
            active: selectedSlot.active,
            minOrderQty: selectedSlot.minOrderQty || 0,
            currOrdersCount: selectedSlot.currOrdersCount || 0
          };
        } else {
          actions.showSnackbar('Selected time slot not found', 'error');
          setSaving(false);
          return;
        }
      }

      const response = await api.put(`/orders/${orderIdToUpdate}`, {
        orderData: updateData
      });

      if (response.data.success) {
        const updateMessages = [];
        if (statusChanged) updateMessages.push('status');
        if (timeSlotChanged) updateMessages.push('time slot');
        actions.showSnackbar(`Order ${updateMessages.join(' and ')} updated successfully`, 'success');
        
        // Call the callback to refresh the orders list
        if (onOrderUpdated) {
          onOrderUpdated();
        }
        onClose();
      } else {
        throw new Error(response.data.message || 'Failed to update order');
      }
    } catch (error) {
      console.error('Error updating order:', error);
      actions.showSnackbar(
        error.response?.data?.message || 'Failed to update order',
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleTransferClick = () => {
    setShowTransfer(!showTransfer);
    if (!showTransfer) {
      setSelectedStore('');
    }
  };

  const handleTimeSlotClick = () => {
    setShowTimeSlotChange(!showTimeSlotChange);
    if (!showTimeSlotChange) {
      setSelectedTimeSlotId(order.timeSlotId || '');
    }
  };

  const handleTransferSubmit = async () => {
    if (!selectedStore) {
      actions.showSnackbar('Please select a store to transfer to', 'error');
      return;
    }

    const selectedStoreData = stores.find(store => store.storeId === selectedStore);
    if (!selectedStoreData) {
      actions.showSnackbar('Selected store not found', 'error');
      return;
    }

    setTransferring(true);
    try {
      const orderIdToUpdate = getOrderId(order);
      const response = await api.put(`/orders/${orderIdToUpdate}/transfer`, {
        newStoreId: selectedStore
      });

      if (response.data.success) {
        actions.showSnackbar('Order transferred successfully', 'success');
        setShowTransfer(false);
        setSelectedStore('');
        
        // Call the callback to refresh the orders list
        if (onOrderUpdated) {
          onOrderUpdated();
        }
        onClose();
      } else {
        throw new Error(response.data.message || 'Failed to transfer order');
      }
    } catch (error) {
      console.error('Error transferring order:', error);
      actions.showSnackbar(
        error.response?.data?.message || 'Failed to transfer order',
        'error'
      );
    } finally {
      setTransferring(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          m: 2
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" fontWeight="bold">
            {/* Displaying Pilot order id */}
            Order Details - #{order.orderId || 'N/A'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ p: 1 }}>
          {/* Customer Information */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
              Customer Information
            </Typography>
            <Card sx={{ backgroundColor: 'grey.50', border: 1, borderColor: 'divider' }}>
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" color="text.secondary">Name:</Typography>
                      {(() => {
                        const chip = getCategoryChip(order.category);
                        return chip ? (
                          <Chip
                            label={chip.label}
                            color={chip.color}
                            size="small"
                            variant="outlined"
                          />
                        ) : null;
                      })()}
                    </Box>
                    <Typography variant="body1" fontWeight="bold">
                      {order.deliveryAddress?.userName || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="text.secondary">Mobile Number:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {order.userId || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="text.secondary">Platform:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {order.order_generation_on || order.platform || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="text.secondary">Channel:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {order.orderFrom || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>

          {/* Order Information */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
              Order Information
            </Typography>
            <Box>
              {/* First Row: Order ID, Order Date, Delivery Date */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Card sx={{ backgroundColor: 'grey.50', border: 1, borderColor: 'divider' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h6" fontWeight="bold">
                        {getOrderId(order)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Order ID
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Card sx={{ backgroundColor: 'grey.50', border: 1, borderColor: 'divider' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h6" fontWeight="bold">
                        {formatDate(getOrderDate(order))}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Order Date
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Card sx={{ backgroundColor: 'grey.50', border: 1, borderColor: 'divider' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h6" fontWeight="bold">
                        {formatDeliveryDate(getDeliveryDate(order))}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Delivery Date
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Card
                    sx={{
                      backgroundColor: 'grey.50',
                      border: isSelfPickup ? 2 : 1,
                      borderColor: isSelfPickup ? 'warning.main' : 'divider'
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h6" fontWeight="bold">
                        {deliveryTypeLabel}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Delivery Type
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              </Box>

              {/* Second Row: Branch & Time Slot (50%) + Status (50%) */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                {/* Left 50%: Branch and Time Slot */}
                <Box sx={{ flex: 1, display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Card sx={{ backgroundColor: 'grey.50', border: 1, borderColor: 'divider' }}>
                      <CardContent sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="h6" fontWeight="bold">
                          {order.storeDetails?.storeName || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Store
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Card sx={{ backgroundColor: 'grey.50', border: 1, borderColor: 'divider' }}>
                      <CardContent sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="h6" fontWeight="bold">
                          {order.timeSlotDetails?.displayTime || 
                           (order.timeSlotDetails ? `${formatTime12Hour(order.timeSlotDetails.startTime)} - ${formatTime12Hour(order.timeSlotDetails.endTime)}` : 'N/A')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Time Slot
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                </Box>
                {/* Right 50%: Status */}
                <Box sx={{ flex: 1 }}>
                  <Card sx={{ 
                    backgroundColor: 'grey.50', 
                    border: 2, 
                    borderColor: 'primary.main'
                  }}>
                    <CardContent sx={{ py: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={orderStatus}
                          onChange={(e) => setOrderStatus(e.target.value)}
                          label="Status"
                        >
                          <MenuItem value="pending">
                            <Chip label="Pending" color="info" size="small" sx={{ minWidth: 70 }} />
                          </MenuItem>
                          <MenuItem value="confirmed">
                            <Chip label="Confirmed" color="warning" size="small" sx={{ minWidth: 70 }} />
                          </MenuItem>
                          <MenuItem value="processing">
                            <Chip label="Processing" color="secondary" size="small" sx={{ minWidth: 70 }} />
                          </MenuItem>
                          <MenuItem value="assigned">
                            <Chip
                              label="Assigned"
                              size="small"
                              sx={{
                                minWidth: 70,
                                bgcolor: '#2e7d32',
                                color: '#fff',
                                '& .MuiChip-label': { fontWeight: 500 }
                              }}
                            />
                          </MenuItem>
                          {order.isSplitParent !== true && (
                            <MenuItem value="out_for_delivery">
                              <Chip 
                                label="Out for Delivery" 
                                size="small" 
                                sx={{ 
                                  minWidth: 70,
                                  backgroundColor: '#15b083',
                                  color: 'white',
                                  '& .MuiChip-label': {
                                    fontWeight: 500
                                  }
                                }} 
                              />
                            </MenuItem>
                          )}
                          {order.isSplitParent !== true && (
                            <MenuItem value="delivered">
                              <Chip label="Delivered" color="success" size="small" sx={{ minWidth: 70 }} />
                            </MenuItem>
                          )}
                          <MenuItem value="partial_delivery">
                            <Chip label="Partial Delivery" color="secondary" size="small" sx={{ minWidth: 70 }} />
                          </MenuItem>
                          <MenuItem value="returned">
                            <Chip label="Returned" color="error" size="small" sx={{ minWidth: 70, backgroundColor: '#ff9800', color: 'white', '& .MuiChip-label': { fontWeight: 500 } }} />
                          </MenuItem>
                          <MenuItem value="cancelled">
                            <Chip label="Cancelled" color="error" size="small" sx={{ minWidth: 70 }} />
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Products Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <OrderIcon sx={{ mr: 1, color: 'primary.main' }} />
              Products ({order.products?.length || 0})
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {(order.products || []).map((product, index) => (
                <Box key={index} sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  p: 2, 
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  backgroundColor: 'background.paper'
                }}>
                  <Avatar 
                    src={product.imageUrl} 
                    alt={product.name}
                    sx={{ width: 60, height: 60, mr: 2 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {product.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box
                        sx={{
                          px: 1.25,
                          py: 0.35,
                          borderRadius: 1,
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          lineHeight: 1.2,
                          flexShrink: 0,
                        }}
                      >
                        <Typography component="span" sx={{ fontSize: '0.95rem', fontWeight: 800 }}>
                          Qty {product.quantity}
                        </Typography>
                      </Box>
                      {product.variant && (
                        <Chip 
                          label={`${product.variant.weight} (+₹${product.variant.price})`}
                          size="small"
                          variant="filled"
                          sx={{ 
                            backgroundColor: 'grey.50',
                            color: 'text.primary',
                            '&:hover': {
                              backgroundColor: 'grey.100'
                            }
                          }}
                        />
                      )}
                      {product.variant?.manualWeight && (
                        <Chip 
                          label={`Manual: ${product.variant.manualWeight}g`}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            borderColor: 'warning.main',
                            color: 'warning.main',
                            '&:hover': {
                              backgroundColor: 'warning.light'
                            }
                          }}
                        />
                      )}
                    </Box>

                    {product.customizations && Object.keys(product.customizations).length > 0 && (
                      <Box sx={{ mt: 1, maxWidth: '500px' }}>
                        {Object.entries(product.customizations).flatMap(([key, customization]) => {
                          const chipSx = {
                            mr: 0.5, mb: 0.5,
                            backgroundColor: 'grey.50',
                            color: 'text.primary',
                            '&:hover': { backgroundColor: 'grey.100' }
                          };

                          // V2 app: additional_customization sent as a plain array
                          if (Array.isArray(customization)) {
                            return customization.map((opt, i) => (
                              <Chip
                                key={`${key}-${i}`}
                                label={`${opt.optionName || opt.name}${parseFloat(opt.additionalPrice) > 0 ? ` (+₹${opt.additionalPrice})` : ''}`}
                                size="small"
                                variant="filled"
                                sx={chipSx}
                              />
                            ));
                          }

                          // Legacy Pilot: checkbox group with selections array
                          if (customization.uiType === 'checkbox' && Array.isArray(customization.selections)) {
                            return customization.selections.map((selection, selectionIndex) => (
                              <Chip
                                key={`${key}-${selectionIndex}`}
                                label={`${selection.optionName || selection.name}${parseFloat(selection.additionalPrice) > 0 ? ` (+₹${selection.additionalPrice})` : ''}`}
                                size="small"
                                variant="filled"
                                sx={chipSx}
                              />
                            ));
                          }

                          // Radio / single-select object
                          const optionName = customization.optionName || customization.name;
                          if (!optionName) return [];
                          const additionalPrice = parseFloat(customization.additionalPrice) || 0;
                          return [
                            <Chip
                              key={key}
                              label={`${optionName}${additionalPrice > 0 ? ` (+₹${additionalPrice})` : ''}`}
                              size="small"
                              variant="filled"
                              sx={chipSx}
                            />
                          ];
                        })}
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    {/* Product Price */}
                    <Typography variant="body2" color="text.secondary">
                      Product: {formatCurrency(product.variant.price * product.quantity)}
                    </Typography>
                    
                    {/* Customizations Total */}
                    {product.customizations && Object.keys(product.customizations).length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Extras: {formatCurrency(Object.values(product.customizations).reduce((sum, c) => {
                          // Handle checkbox customizations (multiple selections)
                          if (c.uiType === 'checkbox' && c.selections) {
                            return sum + c.selections.reduce((selectionSum, selection) => 
                              selectionSum + (parseFloat(selection.additionalPrice) || 0), 0
                            );
                          } else {
                            // Handle radio customizations (single selection)
                            return sum + (parseFloat(c.additionalPrice) || 0);
                          }
                        }, 0) * product.quantity)}
                      </Typography>
                    )}
                    
                    {/* Total Price */}
                    <Typography variant="h6" fontWeight="bold" color="primary.main">
                      {formatCurrency((product.variant.price + (product.customizations ? Object.values(product.customizations).reduce((sum, c) => {
                        // Handle checkbox customizations (multiple selections)
                        if (c.uiType === 'checkbox' && c.selections) {
                          return sum + c.selections.reduce((selectionSum, selection) => 
                            selectionSum + (parseFloat(selection.additionalPrice) || 0), 0
                          );
                        } else {
                          // Handle radio customizations (single selection)
                          return sum + (parseFloat(c.additionalPrice) || 0);
                        }
                      }, 0) : 0)) * product.quantity)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Order Total */}
          <Box sx={{ 
            mb: 4,
            p: 3, 
            backgroundColor: 'grey.50', 
            borderRadius: 2,
            border: 1,
            borderColor: 'divider'
          }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <ReceiptIcon sx={{ mr: 1 }} />
              Order Total
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">Subtotal:</Typography>
              <Typography variant="body1" fontWeight="bold">
                {formatCurrency(order.subtotal || 0)}
              </Typography>
            </Box>
            {order.shippingCost > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Shipping:</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {formatCurrency(order.shippingCost || 0)}
                </Typography>
              </Box>
            )}

            {order.discount > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Coupon Applied: {order.appliedCoupon}</Typography>
                <Typography variant="body1" fontWeight="bold" color="success.main">
                  -{formatCurrency(order.discount || 0)}
                </Typography>
              </Box>
            )}
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h5" fontWeight="bold">Total:</Typography>
              <Typography variant="h5" fontWeight="bold">
                {formatCurrency(getOrderPayableTotal(order))}
              </Typography>
            </Box>
            {order.isSplitParent && order.refundAmount != null && (
              <Box sx={{ mt: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1" fontWeight="bold" color="text.primary">
                    Amount to be refunded
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="text.primary">
                    {formatCurrency(order.refundAmount)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1" fontWeight="bold">
                    Net total
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {formatCurrency(
                      order.netTotal ?? (getOrderPayableTotal(order) - order.refundAmount)
                    )}
                  </Typography>
                </Box>
              </Box>
            )}
            {order.isSplitParent && Array.isArray(order.childOrderIds) && order.childOrderIds.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Child orders
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {order.childOrderIds.map((childId) => (
                    <Chip key={childId} label={`#${childId}`} size="small" variant="outlined" color="info" />
                  ))}
                </Box>
              </Box>
            )}
            {order.parentOrderId && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Child of #{order.parentOrderId}
              </Typography>
            )}
          </Box>

          {/* Instructions Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <NotesIcon sx={{ mr: 1, color: 'primary.main' }} />
              Instructions
            </Typography>
            <Box sx={{ 
              p: 2, 
              backgroundColor: 'grey.50', 
              borderRadius: 2,
              border: 1,
              borderColor: 'divider'
            }}>
                          <Typography variant="body1">
              {order.instructions || 'N/A'}
            </Typography>
            </Box>
          </Box>

          {/* Delivery Address */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <LocationIcon sx={{ mr: 1, color: 'primary.main' }} />
              Delivery Address
            </Typography>
            {order.deliveryAddress && (
              <Box sx={{ 
                p: 3, 
                backgroundColor: 'grey.50', 
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                mb: 2
              }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                  {order.deliveryAddress.userName}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
                  {order.deliveryAddress.houseDetails}
                </Typography>
                {order.deliveryAddress.streetDetails && (
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
                    {order.deliveryAddress.streetDetails}
                  </Typography>
                )}
                {order.deliveryAddress.landmark && (
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
                    {order.deliveryAddress.landmark}
                  </Typography>
                )}
                <Typography variant="body1" color="text.secondary">
                  Pincode: {order.deliveryAddress.pincode}
                </Typography>
              </Box>
            )}

            {/* Delivery Tracking */}
            <Typography variant="h6" sx={{ mb: 1.5, display: 'flex', alignItems: 'center' }}>
              <LocalShippingIcon sx={{ mr: 1, color: 'primary.main' }} />
              Delivery Tracking
            </Typography>
            <Box sx={{ 
              p: 2, 
              backgroundColor: 'grey.50', 
              borderRadius: 2,
              border: 1,
              borderColor: 'divider'
            }}>
              {/* Rider details in single row */}
              <Grid container spacing={3} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Rider name</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {order.deliveryPersonName || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Rider mobile</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {order.deliveryPersonMobile || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Tracking started at</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {order.deliveryTrackingStartedAt
                      ? (() => {
                          const ts = order.deliveryTrackingStartedAt;
                          const date = typeof ts === 'number' ? new Date(ts) : ts?.toDate ? ts.toDate() : ts?._seconds != null ? new Date(ts._seconds * 1000) : new Date(ts);
                          return isNaN(date.getTime()) ? '—' : date.toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' });
                        })()
                      : '—'}
                  </Typography>
                </Grid>
              </Grid>

              {/* Rider tracking map (shown on button click) */}
              {trackingLocations && Array.isArray(trackingLocations) && trackingLocations.length > 0 ? (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<LocationIcon />}
                    onClick={() => setShowTrackingMap((prev) => !prev)}
                    sx={{ mb: showTrackingMap ? 2 : 0 }}
                  >
                    {showTrackingMap ? 'Hide map' : 'View tracking on map'}
                  </Button>
                  {showTrackingMap && (
                    <DeliveryTrackingMap locations={trackingLocations} />
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No location points recorded yet.
                </Typography>
              )}
            </Box>
          </Box>

          {/* Payment Details */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <PaymentIcon sx={{ mr: 1, color: 'text.secondary' }} />
              Payment Details
            </Typography>
            <Box sx={{ 
              p: 2, 
              backgroundColor: 'grey.100', 
              borderRadius: 2,
              border: 1,
              borderColor: 'divider'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Payment Method:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {getPaymentMethod(order)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Payment Status:</Typography>
                <Chip 
                  label={order.paymentStatus.toUpperCase()} 
                  color={order.paymentStatus === 'paid' ? 'success' : 'warning'}
                  size="small"
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Delivery Type:</Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                  {order.delivery_type || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Actions Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <ActionsIcon sx={{ mr: 1, color: 'primary.main' }} />
              Actions
            </Typography>
            {!isSuperAdmin ? (
              <Box sx={{
                p: 2,
                backgroundColor: 'info.50',
                borderRadius: 2,
                border: 1,
                borderColor: 'info.main'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <InfoIcon sx={{ mr: 1, color: 'info.main' }} />
                  <Typography variant="body2" color="info.main" fontWeight="bold">
                    Actions Not Available
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Store transfer and time slot changes are restricted to super admin
                </Typography>
              </Box>
            ) : order.orderFrom === 'PILOT' && getOrderStatus(order) === 'pending' ? (
              <Box sx={{ 
                p: 2, 
                backgroundColor: 'grey.50', 
                borderRadius: 2,
                border: 1,
                borderColor: 'divider'
              }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<TransferIcon />}
                    onClick={handleTransferClick}
                    sx={{ mb: showTransfer ? 2 : 0 }}
                  >
                    Transfer Order
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<TimeSlotIcon />}
                    onClick={handleTimeSlotClick}
                    sx={{ mb: showTimeSlotChange ? 2 : 0 }}
                  >
                    Change Time Slot
                  </Button>
                  {isSuperAdmin &&
                    onPartialDelivery &&
                    (getOrderStatus(order) === 'pending' || getOrderStatus(order) === 'confirmed') &&
                    (order.products?.length || 0) >= 2 &&
                    order.isSplitParent !== true && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<PartialDeliveryIcon />}
                      onClick={() => {
                        const current = order;
                        onClose();
                        onPartialDelivery(current);
                      }}
                    >
                      Partial Delivery
                    </Button>
                  )}
                </Box>
              
              {showTransfer && (
                <Box sx={{
                  p: 2,
                  backgroundColor: 'background.paper',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider'
                }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Transfer this order to another store:
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={8}>
                      <FormControl fullWidth>
                        <InputLabel>Select New Store</InputLabel>
                        <Select
                          value={selectedStore}
                          onChange={(e) => setSelectedStore(e.target.value)}
                          label="Select New Store"
                          disabled={transferring}
                          sx={{width: '200px'}}
                        >
                          {stores
                            .filter(store => store.storeId !== order.storeId) // Exclude current store
                            .map((store) => (
                              <MenuItem key={store.storeId} value={store.storeId}>
                                {store.storeName} 
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Button
                        variant="contained"
                        onClick={handleTransferSubmit}
                        disabled={!selectedStore || transferring}
                        startIcon={transferring ? <CircularProgress size={20} /> : <TransferIcon />}
                        fullWidth
                      >
                        {transferring ? 'Transferring...' : 'Transfer'}
                      </Button>
                    </Grid>
                  </Grid>
                  {selectedStore && (
                    <Box sx={{ mt: 2, p: 1, backgroundColor: 'info.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="info.main">
                        Current Store: {order.storeDetails?.storeName || 'N/A'} → 
                        New Store: {stores.find(s => s.storeId === selectedStore)?.storeName || 'N/A'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {showTimeSlotChange && (
                <Box sx={{
                  p: 2,
                  backgroundColor: 'background.paper',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                  mt: showTransfer ? 2 : 0
                }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Change time slot for this order:
                  </Typography>
                  {timeSlotsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={8}>
                        <FormControl fullWidth>
                          <InputLabel>Select Time Slot</InputLabel>
                          <Select
                            value={selectedTimeSlotId || ''}
                            onChange={(e) => setSelectedTimeSlotId(e.target.value)}
                            label="Select Time Slot"
                            disabled={saving}
                            sx={{ width: '200px' }}
                          >
                            {timeSlots.length === 0 ? (
                              <MenuItem value="" disabled>
                                No slots available
                              </MenuItem>
                            ) : (
                              timeSlots.map((slot) => (
                                <MenuItem 
                                  key={slot.id} 
                                  value={slot.id}
                                  disabled={slot.isDisabled}
                                >
                                  {slot.displayTime || `${formatTime12Hour(slot.startTime)} - ${formatTime12Hour(slot.endTime)}`}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>
                      </Grid>
                      {/* <Grid item xs={12} sm={4}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="outlined"
                            onClick={handleTimeSlotClick}
                            disabled={saving}
                            fullWidth
                          >
                            Cancel
                          </Button>
                        </Box>
                      </Grid> */}
                    </Grid>
                  )}
                  {selectedTimeSlotId && selectedTimeSlotId !== (order.timeSlotId || '') && (
                    <Box sx={{ mt: 2, p: 1, backgroundColor: 'info.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="info.main">
                        Current: {order.timeSlotDetails?.displayTime || 
                          (order.timeSlotDetails ? `${formatTime12Hour(order.timeSlotDetails.startTime)} - ${formatTime12Hour(order.timeSlotDetails.endTime)}` : 'N/A')} → 
                        New: {timeSlots.find(s => s.id === selectedTimeSlotId)?.displayTime || 
                          (() => {
                            const slot = timeSlots.find(s => s.id === selectedTimeSlotId);
                            return slot ? `${formatTime12Hour(slot.startTime)} - ${formatTime12Hour(slot.endTime)}` : 'N/A';
                          })()}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
              </Box>
            ) : (
              <Box sx={{ 
                p: 2, 
                backgroundColor: 'grey.50', 
                borderRadius: 2,
                border: 1,
                borderColor: 'divider'
              }}>
                {order.orderFrom === 'PILOT' ? (
                  <>
                    {onPartialDelivery &&
                      (getOrderStatus(order) === 'confirmed') &&
                      (order.products?.length || 0) >= 2 &&
                      order.isSplitParent !== true && (
                      <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<PartialDeliveryIcon />}
                        onClick={() => {
                          const current = order;
                          onClose();
                          onPartialDelivery(current);
                        }}
                        sx={{ mb: 2 }}
                      >
                        Partial Delivery
                      </Button>
                    )}
                    <Box sx={{ mb: 2, p: 1, backgroundColor: 'info.50', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <InfoIcon sx={{ mr: 1, color: 'info.main' }} />
                        <Typography variant="body2" color="info.main" fontWeight="bold">
                          Store Transfer Not Available
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Only pending orders can be transferred
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      startIcon={<TimeSlotIcon />}
                      onClick={handleTimeSlotClick}
                      sx={{ mb: showTimeSlotChange ? 2 : 0 }}
                    >
                      Change Time Slot
                    </Button>
                    {showTimeSlotChange && (
                      <Box sx={{
                        p: 2,
                        backgroundColor: 'background.paper',
                        borderRadius: 1,
                        border: 1,
                        borderColor: 'divider',
                        mt: 2
                      }}>
                        <Typography variant="subtitle2" sx={{ mb: 2 }}>
                          Change time slot for this order:
                        </Typography>
                        {timeSlotsLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : (
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={8}>
                              <FormControl fullWidth>
                                <InputLabel>Select Time Slot</InputLabel>
                                <Select
                                  value={selectedTimeSlotId || ''}
                                  onChange={(e) => setSelectedTimeSlotId(e.target.value)}
                                  label="Select Time Slot"
                                  disabled={saving}
                                  sx={{ width: '200px' }}
                                >
                                  {timeSlots.length === 0 ? (
                                    <MenuItem value="" disabled>
                                      No slots available
                                    </MenuItem>
                                  ) : (
                                    timeSlots.map((slot) => (
                                      <MenuItem 
                                        key={slot.id} 
                                        value={slot.id}
                                        disabled={slot.isDisabled}
                                      >
                                        {slot.displayTime || `${formatTime12Hour(slot.startTime)} - ${formatTime12Hour(slot.endTime)}`}
                                      </MenuItem>
                                    ))
                                  )}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  variant="outlined"
                                  onClick={handleTimeSlotClick}
                                  disabled={saving}
                                  fullWidth
                                >
                                  Cancel
                                </Button>
                              </Box>
                            </Grid>
                          </Grid>
                        )}
                        {selectedTimeSlotId && selectedTimeSlotId !== (order.timeSlotId || '') && (
                          <Box sx={{ mt: 2, p: 1, backgroundColor: 'info.50', borderRadius: 1 }}>
                            <Typography variant="caption" color="info.main">
                              Current: {order.timeSlotDetails?.displayTime || 
                                (order.timeSlotDetails ? `${formatTime12Hour(order.timeSlotDetails.startTime)} - ${formatTime12Hour(order.timeSlotDetails.endTime)}` : 'N/A')} → 
                              New: {timeSlots.find(s => s.id === selectedTimeSlotId)?.displayTime || 
                                (() => {
                                  const slot = timeSlots.find(s => s.id === selectedTimeSlotId);
                                  return slot ? `${formatTime12Hour(slot.startTime)} - ${formatTime12Hour(slot.endTime)}` : 'N/A';
                                })()}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </>
                ) : (
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: 'info.50', 
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'info.main'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <InfoIcon sx={{ mr: 1, color: 'info.main' }} />
                      <Typography variant="body2" color="info.main" fontWeight="bold">
                        Actions Not Available
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Only orders from PILOT system can be modified
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* Transfer History */}
          {order.transferHistory && order.transferHistory.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <TransferIcon sx={{ mr: 1, color: 'warning.main' }} />
                Transfer History
              </Typography>
              <Box sx={{ 
                p: 2, 
                backgroundColor: 'warning.50', 
                borderRadius: 2,
                border: 1,
                borderColor: 'warning.main'
              }}>
                {order.transferHistory.map((transfer, index) => (
                  <Box key={index} sx={{ 
                    mb: index < order.transferHistory.length - 1 ? 2 : 0,
                    pb: index < order.transferHistory.length - 1 ? 2 : 0,
                    borderBottom: index < order.transferHistory.length - 1 ? 1 : 0,
                    borderBottomColor: 'warning.light'
                  }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Transfer #{index + 1}:</strong> {transfer.fromStoreName} → {transfer.toStoreName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Transferred on: {formatDate(transfer.transferredAt)}
                      {transfer.transferredBy && ` by ${transfer.transferredBy}`}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Prana Integration Details */}
          {order.pranaDetails && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <StoreIcon sx={{ mr: 1, color: 'primary.main' }} />
                Prana Integration Details
              </Typography>
              <Box sx={{ 
                p: 2, 
                backgroundColor: order.pranaDetails.status === 'success' ? 'success.50' : 'error.50', 
                borderRadius: 2,
                border: 1,
                borderColor: order.pranaDetails.status === 'success' ? 'success.main' : 'error.main'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Status:</Typography>
                  <Chip 
                    label={order.pranaDetails.status?.toUpperCase() || 'N/A'} 
                    color={order.pranaDetails.status === 'success' ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
                {order.pranaDetails.orderId && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Prana Order ID:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {order.pranaDetails.orderId}
                    </Typography>
                  </Box>
                )}
                {order.pranaDetails.updatedAt && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Last Updated:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {formatDate(order.pranaDetails.updatedAt)}
                    </Typography>
                  </Box>
                )}
                {order.pranaDetails.error && (
                  <Box sx={{ mt: 2, p: 1, backgroundColor: 'error.100', borderRadius: 1 }}>
                    <Typography variant="body2" color="error.main" fontWeight="bold">
                      Error: {order.pranaDetails.error}
                    </Typography>
                  </Box>
                )}
                {order.pranaDetails.response && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Response Details:
                    </Typography>
                    <Box sx={{ 
                      p: 1, 
                      backgroundColor: 'background.paper', 
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'divider',
                      maxHeight: 150,
                      overflow: 'auto'
                    }}>
                      <Typography variant="caption" component="pre" sx={{ 
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        {JSON.stringify(order.pranaDetails.response, null, 2)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Pidge Integration Details */}
          {order.pidgeDetails && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <LocalShippingIcon sx={{ mr: 1, color: 'secondary.main' }} />
                Pidge Integration Details
              </Typography>
              <Box sx={{ 
                p: 2, 
                backgroundColor: order.pidgeDetails.status === 'success' ? 'success.50' : 'error.50', 
                borderRadius: 2,
                border: 1,
                borderColor: order.pidgeDetails.status === 'success' ? 'success.main' : 'error.main'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Status:</Typography>
                  <Chip 
                    label={order.pidgeDetails.status?.toUpperCase() || 'N/A'} 
                    color={order.pidgeDetails.status === 'success' ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
                {order.pidgeDetails.orderId && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Pidge Order ID:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {order.pidgeDetails.orderId}
                    </Typography>
                  </Box>
                )}
                {order.pidgeDetails.updatedAt && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Last Updated:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {formatDate(order.pidgeDetails.updatedAt)}
                    </Typography>
                  </Box>
                )}
                {order.pidgeDetails.error && (
                  <Box sx={{ mt: 2, p: 1, backgroundColor: 'error.100', borderRadius: 1 }}>
                    <Typography variant="body2" color="error.main" fontWeight="bold">
                      Error: {order.pidgeDetails.error}
                    </Typography>
                  </Box>
                )}
                {order.pidgeDetails.response && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Response Details:
                    </Typography>
                    <Box sx={{ 
                      p: 1, 
                      backgroundColor: 'background.paper', 
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'divider',
                      maxHeight: 150,
                      overflow: 'auto'
                    }}>
                      <Typography variant="caption" component="pre" sx={{ 
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        {JSON.stringify(order.pidgeDetails.response, null, 2)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Zithara Integration Details */}
          {order.zitharaDetails && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <CardGiftcardIcon sx={{ mr: 1, color: 'secondary.main' }} />
                Zithara Integration Details
              </Typography>
              <Box sx={{
                p: 2,
                backgroundColor: order.zitharaDetails.status === 'success' ? 'success.50' : 'error.50',
                borderRadius: 2,
                border: 1,
                borderColor: order.zitharaDetails.status === 'success' ? 'success.main' : 'error.main'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Status:</Typography>
                  <Chip
                    label={order.zitharaDetails.status?.toUpperCase() || 'N/A'}
                    color={order.zitharaDetails.status === 'success' ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
                {order.zitharaDetails.transactionId && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Transaction ID:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {order.zitharaDetails.transactionId}
                    </Typography>
                  </Box>
                )}
                {order.zitharaDetails.updatedAt && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Last Updated:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {formatDate(order.zitharaDetails.updatedAt)}
                    </Typography>
                  </Box>
                )}
                {order.zitharaDetails.error && (
                  <Box sx={{ mt: 2, p: 1, backgroundColor: 'error.100', borderRadius: 1 }}>
                    <Typography variant="body2" color="error.main" fontWeight="bold">
                      Error: {order.zitharaDetails.error}
                    </Typography>
                  </Box>
                )}
                {order.zitharaDetails.response && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Response Details:
                    </Typography>
                    <Box sx={{
                      p: 1,
                      backgroundColor: 'background.paper',
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'divider',
                      maxHeight: 150,
                      overflow: 'auto'
                    }}>
                      <Typography variant="caption" component="pre" sx={{
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        {JSON.stringify(order.zitharaDetails.response, null, 2)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Status Reason */}
          {order.statusReason && (order.order_status === 'cancelled' || order.order_status === 'returned') && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <NotesIcon sx={{ mr: 1, color: order.order_status === 'cancelled' ? 'error.main' : 'warning.main' }} />
                {order.order_status === 'cancelled' ? 'Cancellation' : 'Return'} Reason
              </Typography>
              <Box sx={{
                p: 2,
                backgroundColor: order.order_status === 'cancelled' ? 'error.50' : 'warning.50',
                borderRadius: 2,
                border: 1,
                borderColor: order.order_status === 'cancelled' ? 'error.main' : 'warning.main'
              }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {order.statusReason}
                </Typography>
                {order.statusReasonTimestamp && (
                  <Typography variant="caption" color="text.secondary">
                    Provided on: {formatDate(order.statusReasonTimestamp)}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        <Button 
          onClick={handleSaveStatus} 
          variant="contained" 
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          disabled={saving || (orderStatus === getOrderStatus(order) && (!selectedTimeSlotId || selectedTimeSlotId === (order.timeSlotId || '')))}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderModal; 